import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Box } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { Modal } from "components/haven-design/modal";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import {
  ElasticQuery,
  assignInterestOwnersBulk,
  assignInterestOwnersBulkVariables,
} from "types/gql-types";
import { ASSIGN_INTEREST_OWNERS_BULK } from "pages/dashboard/interests-profile/components/actions-query";
import AccessGrantWarning from "components/common/owners-editor/access-grant-warning";
import { ASSIGNABLE_OWNERS } from "components/common/owners-editor/owner-queries";
import OwnerSelect, {
  AssignableOwnersData,
  accessGrantPropertyName,
  buildInterestListNameMap,
  buildOptions,
  ownerManagesAll,
  ownersFromData,
} from "components/common/owners-editor/owner-select";
import { profileOwnerLabel } from "components/common/owners-editor/owner-names";
import BulkResultSummary from "./bulk-result-summary";
import { BulkOwnerError, BulkOwnerProgress, useBulkOwnerAction } from "./bulk-owner-feedback";
import { MAX_BULK_OWNERS, countOf } from "./utils";

const summaryLabel = (ownerCount: number, applicantCount: number) => {
  const applications = countOf(applicantCount, "application");
  if (ownerCount === 0) return `Add owners to ${applications}`;
  return `Add ${countOf(ownerCount, "owner")} to ${applications}`;
};

export type AssignOwnerModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  onCancel: () => void;

  /** Number of selected applicants — shown in the summary line. */
  selectedCount: number;

  /** Current user, used for the pinned "Me (Your Name)" option. */
  currentUserId: string;
  currentUserName: string;
  interestListIds: string[];
  interestIds: string[] | null;
  elasticQuery: ElasticQuery | null;
  pageFrom: number | null;
  pageTo: number | null;
  onSuccess?: () => void;
  onResult?: (succeeded: string[], failed: string[]) => void;
};

const AssignOwnerModal: React.FC<AssignOwnerModalProps> = ({
  isOpen,
  onRequestClose,
  onCancel,
  selectedCount,
  currentUserId,
  currentUserName,
  interestListIds,
  interestIds,
  elasticQuery,
  pageFrom,
  pageTo,
  onSuccess,
  onResult,
}) => {
  const { data, loading: ownersLoading } = useQuery<AssignableOwnersData>(ASSIGNABLE_OWNERS, {
    skip: !isOpen,
    // cache-and-network so a just-created owner shows on the next open without a
    // full page reload (cached list renders instantly, then refetches).
    fetchPolicy: "cache-and-network",
  });

  const [assignOwners, { loading: isSubmitting }] = useMutation<
  assignInterestOwnersBulk,
  assignInterestOwnersBulkVariables
  >(ASSIGN_INTEREST_OWNERS_BULK);

  const {
    selectedOwners,
    setSelectedOwners,
    failureResult,
    errorMessage,
    submit,
    retry,
    close,
    cancel,
  } = useBulkOwnerAction({
    mutate: (variables) => assignOwners({ variables })
      .then((res) => res.data?.assignInterestOwnersBulk),
    isSubmitting,
    interestListIds,
    scope: { interestIds, elasticQuery, pageFrom, pageTo },
    // Emitted on every answer so the parent's success handling runs even on the
    // zero-failure case.
    onResult: ({ succeeded, failed }) => {
      onResult?.(succeeded, failed.map((entry) => entry.interestId));
      if (failed.length === 0) onSuccess?.();
    },
    onRequestClose,
    onCancel,
  });

  const owners = React.useMemo(() => ownersFromData(data), [data]);
  const interestListNameMap = React.useMemo(() => buildInterestListNameMap(owners), [owners]);

  // HAV-58 — float the scoped property's group to the top, but only when EXACTLY
  // ONE distinct known property is in scope; otherwise null -> alphabetical.
  const currentPropertyName = React.useMemo<string | null>(() => {
    const names = Array.from(
      new Set(
        interestListIds
          .map((id) => interestListNameMap.get(id))
          .filter((name): name is string => Boolean(name)),
      ),
    );
    return names.length === 1 ? names[0] : null;
  }, [interestListIds, interestListNameMap]);

  const options = React.useMemo(
    () => buildOptions(owners, currentUserId, currentUserName, currentPropertyName),
    [owners, currentUserId, currentUserName, currentPropertyName],
  );

  // One warning per chosen owner who doesn't already manage the selection's property.
  const ownersGainingAccess = React.useMemo(
    () => selectedOwners.flatMap((option) => {
      const owner = owners.find((candidate) => candidate.id === option.userId);
      return owner && !ownerManagesAll(owner, interestListIds) ? [owner] : [];
    }),
    [selectedOwners, owners, interestListIds],
  );

  const propertyName = accessGrantPropertyName(interestListIds, interestListNameMap);

  if (failureResult) {
    return (
      <Modal title="Assign Owner" isOpen={isOpen} onRequestClose={close} fullWidth>
        <BulkResultSummary
          result={failureResult}
          onRetry={retry}
          onClose={close}
          isRetrying={isSubmitting}
          error={errorMessage && <BulkOwnerError id="assign-owner-error" message={errorMessage} />}
        />
      </Modal>
    );
  }

  return (
    <Modal title="Assign Owner" isOpen={isOpen} onRequestClose={close} fullWidth>
      <DialogContent>
        <Box component="p" id="assign-owner-summary" sx={{ mb: 0 }}>
          {summaryLabel(selectedOwners.length, selectedCount)}
        </Box>
        <Box component="p" sx={{ mt: 0.5, color: "text.secondary" }}>
          Existing owners are kept.
        </Box>
        <OwnerSelect
          multiple
          id="assign-owner-select"
          options={options}
          loading={ownersLoading}
          value={selectedOwners}
          disabled={isSubmitting}
          onChange={setSelectedOwners}
          maxSelections={MAX_BULK_OWNERS}
        />
        {ownersGainingAccess.map((owner) => (
          <Box key={owner.id} data-cy={`assign-owner-access-warning-${owner.id}`}>
            <AccessGrantWarning
              ownerName={profileOwnerLabel(owner.profile)}
              propertyName={propertyName}
              sx={{ mt: 1.5, mb: 0 }}
            />
          </Box>
        ))}
        {isSubmitting && (
          <BulkOwnerProgress id="assign-owner-progress" message="Adding owners…" />
        )}
        {errorMessage && <BulkOwnerError id="assign-owner-error" message={errorMessage} />}
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          size="S"
          onClick={cancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          size="S"
          onClick={submit}
          disabled={isSubmitting || selectedOwners.length === 0}
        >
          {isSubmitting ? "Submitting" : "Assign"}
        </Button>
      </DialogActions>
    </Modal>
  );
};

export default AssignOwnerModal;
